import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import type {
  FaturaDoProcesso,
  LinhaAr,
  PaginaFinanceiro,
  ResumoFinanceiroProcesso,
} from '../types';
import {
  anexarComprovanteProcesso,
  aplicarDescontoProcesso,
  cancelarFaturaProcesso,
  enviarSegundaViaProcesso,
  estornarPagamentoProcesso,
  fetchArsProcesso,
  fetchFaturasProcesso,
  fetchResumoFinanceiro,
  reativarConsumoProcesso,
  solicitarEstornoProcesso,
  zerarConsumoProcesso,
} from './actions';

export interface ColecaoPaginada<T> {
  items: T[];
  page: number;
  total: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

export interface EstadoFinanceiroProcesso {
  resumo: ResumoFinanceiroProcesso | null;
  resumoLoading: boolean;
  resumoError: string | null;
  faturas: ColecaoPaginada<FaturaDoProcesso>;
  ars: ColecaoPaginada<LinhaAr>;
  salvando: boolean;
  erroAcao: string | null;
}

export interface FinanceiroProcessoArbitralState {
  byProcessoId: Record<string, EstadoFinanceiroProcesso>;
}

const colecaoVazia = <T>(): ColecaoPaginada<T> => ({
  items: [],
  page: 0,
  total: 0,
  totalPages: 0,
  loading: false,
  error: null,
});

export const estadoVazio = (): EstadoFinanceiroProcesso => ({
  resumo: null,
  resumoLoading: false,
  resumoError: null,
  faturas: colecaoVazia<FaturaDoProcesso>(),
  ars: colecaoVazia<LinhaAr>(),
  salvando: false,
  erroAcao: null,
});

const initialState: FinanceiroProcessoArbitralState = {
  byProcessoId: {},
};

function estadoDo(
  state: FinanceiroProcessoArbitralState,
  processoId: string,
): EstadoFinanceiroProcesso {
  state.byProcessoId[processoId] ??= estadoVazio();
  return state.byProcessoId[processoId];
}

function aplicarPagina<T extends { id?: string; eventoId?: string }>(
  colecao: ColecaoPaginada<T>,
  pagina: PaginaFinanceiro<T>,
  acumular: boolean,
): void {
  const chave = (item: T) => item.id ?? item.eventoId ?? '';
  const base = acumular ? colecao.items : [];
  const vistos = new Set(base.map(chave));
  colecao.items = [...base, ...pagina.itens.filter((item) => !vistos.has(chave(item)))];
  colecao.page = pagina.page;
  colecao.total = pagina.total;
  colecao.totalPages = pagina.totalPages;
  colecao.loading = false;
  colecao.error = null;
}

const financeiroProcessoArbitralSlice = createSlice({
  name: 'financeiroProcessoArbitral',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchResumoFinanceiro.pending, (state, action) => {
        const estado = estadoDo(state, action.meta.arg);
        estado.resumoLoading = true;
        estado.resumoError = null;
      })
      .addCase(fetchResumoFinanceiro.fulfilled, (state, action) => {
        const estado = estadoDo(state, action.payload.processoId);
        estado.resumoLoading = false;
        estado.resumo = action.payload.resumo;
      })
      .addCase(fetchResumoFinanceiro.rejected, (state, action) => {
        const estado = estadoDo(state, action.meta.arg);
        estado.resumoLoading = false;
        estado.resumoError = action.payload?.mensagem ?? 'Erro ao carregar o financeiro';
      })

      .addCase(fetchFaturasProcesso.pending, (state, action) => {
        const estado = estadoDo(state, action.meta.arg.processoId);
        estado.faturas.loading = true;
        estado.faturas.error = null;
      })
      .addCase(fetchFaturasProcesso.fulfilled, (state, action) => {
        const estado = estadoDo(state, action.payload.processoId);
        aplicarPagina(estado.faturas, action.payload.pagina, action.payload.acumular);
      })
      .addCase(fetchFaturasProcesso.rejected, (state, action) => {
        const estado = estadoDo(state, action.meta.arg.processoId);
        estado.faturas.loading = false;
        estado.faturas.error = action.payload?.mensagem ?? 'Erro ao carregar as faturas';
      })

      .addCase(fetchArsProcesso.pending, (state, action) => {
        const estado = estadoDo(state, action.meta.arg.processoId);
        estado.ars.loading = true;
        estado.ars.error = null;
      })
      .addCase(fetchArsProcesso.fulfilled, (state, action) => {
        const estado = estadoDo(state, action.payload.processoId);
        aplicarPagina(estado.ars, action.payload.pagina, action.payload.acumular);
      })
      .addCase(fetchArsProcesso.rejected, (state, action) => {
        const estado = estadoDo(state, action.meta.arg.processoId);
        estado.ars.loading = false;
        estado.ars.error = action.payload?.mensagem ?? 'Erro ao carregar os ARs';
      });

    builder
      .addMatcher(
        isAnyOf(
          cancelarFaturaProcesso.pending,
          enviarSegundaViaProcesso.pending,
          anexarComprovanteProcesso.pending,
          aplicarDescontoProcesso.pending,
          estornarPagamentoProcesso.pending,
          solicitarEstornoProcesso.pending,
          zerarConsumoProcesso.pending,
          reativarConsumoProcesso.pending,
        ),
        (state, action) => {
          const estado = estadoDo(state, action.meta.arg.processoId);
          estado.salvando = true;
          estado.erroAcao = null;
        },
      )
      .addMatcher(
        isAnyOf(
          cancelarFaturaProcesso.fulfilled,
          enviarSegundaViaProcesso.fulfilled,
          anexarComprovanteProcesso.fulfilled,
          aplicarDescontoProcesso.fulfilled,
          estornarPagamentoProcesso.fulfilled,
          solicitarEstornoProcesso.fulfilled,
          zerarConsumoProcesso.fulfilled,
          reativarConsumoProcesso.fulfilled,
        ),
        (state, action) => {
          estadoDo(state, action.meta.arg.processoId).salvando = false;
        },
      )
      .addMatcher(
        isAnyOf(
          cancelarFaturaProcesso.rejected,
          enviarSegundaViaProcesso.rejected,
          anexarComprovanteProcesso.rejected,
          aplicarDescontoProcesso.rejected,
          estornarPagamentoProcesso.rejected,
          solicitarEstornoProcesso.rejected,
          zerarConsumoProcesso.rejected,
          reativarConsumoProcesso.rejected,
        ),
        (state, action) => {
          const estado = estadoDo(state, action.meta.arg.processoId);
          estado.salvando = false;
          estado.erroAcao = action.payload?.mensagem ?? 'Não foi possível concluir a ação';
        },
      );
  },
});

export const financeiroProcessoArbitralReducer = financeiroProcessoArbitralSlice.reducer;
